/**
 * 文字逐字漸顯與上浮動畫模組 (Sequential Fade-in & Slide-up Text Reveal)
 * * @module animations
 * * ==========================================
 * 【套件依賴 (Dependencies)】
 * 1. GSAP 3.x up。
 * 2. IntersectionObserver API: 
 * - 原生 Web API，用於監聽元素是否進入畫面（目前主流瀏覽器皆支援）。
 * * ==========================================
 * 【使用方式 (Usage)】
 * * class `fade-text`。
 * <h2 class="fade-text">這是需要漸顯的文字</h2>
 * * 2. CSS 建議 (防止 FOUT 閃爍)：
 * 建議在全域 CSS 中先將其透明度設為 0，避免 JS 載入前文字就先曝露。
 * .fade-text { opacity: 0; } 
 * * 3. JS 初始化：
 * import { initFadeTextAnimation } from './modules/animations.js';
 * initFadeTextAnimation();
 * ==========================================
 */

function initFadeTextAnimation() {

  const fadeTexts = document.querySelectorAll(".fade-text");
  if (fadeTexts.length === 0) return;

  // 初始化狀態：標記頁面是否剛載入
  let isInitialLoad = true;
  setTimeout(() => { isInitialLoad = false; }, 200);

  // 文字拆分邏輯 (優化：合併 DocumentFragment 處理)
  const splitToSpans = (textNode) => {
    const text = textNode.nodeValue || "";
    const frag = document.createDocumentFragment();

    /**
     * 將英文單字分為一組(避免單字字母分行)
     * 正規表達式分詞說明：
     * ([a-zA-Z]+) : 匹配連續的英文字母（即英文單字）
     * (\s+)       : 匹配連續的空白字元（包含空格、換行等）
     * (.)         : 匹配上述條件以外的任意單一字元（如中文字、數字、標點符號）
     * 旗標 'gu'   : 'g' 表示全域搜尋，'u' 表示啟用 Unicode 支援（確保正確解析 Emoji 等特殊字元）
     */
    const tokens = text.match(/([a-zA-Z]+)|(\s+)|(.)/gu) || [];
    tokens.forEach(token => {
      // 檢查此 Token 是否為純英文單字
      if (/^[a-zA-Z]+$/.test(token)) {
        // 1. 英文字：建立外層單字包裹器（使用符合段落內容語意的 span，而非區塊級 div）
        const wordWrapper = document.createElement("span");
        wordWrapper.classList.add("inline-block");
        // 強制維持行內區塊與不換行，確保單字在斷行時不會被碎解成兩半
        wordWrapper.style.cssText = "display: inline-block; white-space: nowrap;";

        // 拆分該英文單字內的每一個字母 (Character)
        for (const ch of token) {
          const span = document.createElement("span");
          span.setAttribute("aria-hidden", "true"); // 對螢幕閱讀器隱藏碎片化節點
          span.style.cssText = "display:inline-block; opacity:0; will-change:transform, opacity;";
          span.textContent = ch;
          wordWrapper.appendChild(span);
        }
        // 將包裝完整的單字結構塞入文件片段
        frag.appendChild(wordWrapper);

      } else if (/^\s+$/.test(token)) {
        // 2. 空白字元：確保英文單字之間、或中英夾雜間的空格被正確還原
        for (const ch of token) {
          const span = document.createElement("span");
          span.setAttribute("aria-hidden", "true");
          span.style.cssText = "display:inline-block; opacity:0; will-change:transform, opacity;";
          // 將標準空格轉換為網頁實體不換行空格 (&nbsp;)，避免被瀏覽器預設壓縮忽略
          span.innerHTML = (ch === " ") ? "&nbsp;" : ch;
          frag.appendChild(span);
        }

      } else {
        // 3. 其他字元（如中文字、數字、標點符號）：無需單字外殼，直接逐字拆分
        for (const ch of token) {
          const span = document.createElement("span");
          span.setAttribute("aria-hidden", "true");
          span.style.cssText = "display:inline-block; opacity:0; will-change:transform, opacity;";
          span.textContent = ch;
          frag.appendChild(span);
        }
      }
    });

    return frag;
  };

  // 遍歷所有文字容器，進行初始化與 IntersectionObserver 綁定
  fadeTexts.forEach((container) => {
    // A11y 處理
    const content = container.innerText.replace(/\s+/g, " ").trim();
    if (!content) return;
    container.setAttribute("aria-label", content);

    // 取得文字節點並替換
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    const nodes = [];
    let n;
    while (n = walker.nextNode()) {
      if (n.nodeValue.trim().length > 0) nodes.push(n);
    }

    nodes.forEach(node => {
      node.parentNode.replaceChild(splitToSpans(node), node);
    });

    // 動態計算 Margin
    const startPercent = parseFloat(container.getAttribute('data-fadeT-start')) || 90;
    const rootMargin = `0px 0px -${100 - startPercent}% 0px`;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const spans = container.querySelectorAll("span[aria-hidden='true']");

          gsap.set(container, { opacity: 1 });

          gsap.fromTo(spans,
            {
              opacity: 0,
              x: 24, // 使用 x 代替 xPercent 在逐字動畫中通常更穩定
              force3D: true
            },
            {
              opacity: 1,
              x: 0,
              delay: isInitialLoad ? 0.4 : 0,
              duration: 0.8,
              ease: "power2.out",
              stagger: 0.03, // 稍微縮短間隔，視覺感更流暢
              onComplete: () => {
                // 動畫結束後移除 will-change 節省資源
                gsap.set(spans, { clearProps: "will-change" });
              }
            }
          );
          observer.unobserve(container);
        }
      });
    }, { rootMargin, threshold: 0.1 });

    observer.observe(container);
  });
}

export { initFadeTextAnimation };